import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Stitch Fix pattern: unlock concierge autopilot after 3 manual approvals. */
export const MANUAL_APPROVALS_FOR_AUTOPILOT = 3;

export interface ManualApprovalProgress {
  count: number;
  threshold: number;
  unlocked: boolean;
  remaining: number;
}

async function fetchManualApprovalCount(companyId: string): Promise<number> {
  const { data: celebrations, error: celErr } = await supabase
    .from("celebrations")
    .select("id")
    .eq("company_id", companyId);
  if (celErr) throw celErr;
  const ids = (celebrations ?? []).map((c) => c.id);
  if (ids.length === 0) return 0;

  const { count, error } = await supabase
    .from("celebration_recommendations")
    .select("id", { count: "exact", head: true })
    .in("celebration_id", ids)
    .eq("status", "approved")
    .not("approved_by", "is", null);
  if (error) throw error;
  return count ?? 0;
}

export function useManualApprovalProgress(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["companies", companyId, "manual-approval-progress"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<ManualApprovalProgress> => {
      if (!companyId) {
        return {
          count: 0,
          threshold: MANUAL_APPROVALS_FOR_AUTOPILOT,
          unlocked: false,
          remaining: MANUAL_APPROVALS_FOR_AUTOPILOT,
        };
      }
      const count = await fetchManualApprovalCount(companyId);
      const threshold = MANUAL_APPROVALS_FOR_AUTOPILOT;
      const unlocked = count >= threshold;
      return {
        count,
        threshold,
        unlocked,
        remaining: Math.max(0, threshold - count),
      };
    },
  });
}
